/**
 * Production-neutral weapon basic-attack timing bridge, shared by the Balance
 * Lab combat kernel and the `AuthoritativeWorld` weapon basic attack so
 * neither depends on the other. `src/experiments/balance-combat-v0.ts` rebuilds
 * `BALANCE_COMBAT_BRIDGE_V0` from this table.
 *
 * This owns NO attack rate and NO hit damage. `cycleS` authority stays with
 * `resolveSourceProfile()`; `castShareOfCycle` owns only the pre-hit/post-hit
 * split. `AuthoritativeWorld` (STRUCT.recovery Task 5, R5) now realizes this
 * split as an observable WINDUP/ACTIVE/RECOVERY action-phase state machine —
 * `castShareOfCycle` is that machine's COMPATIBILITY SEED (it still owns the
 * only numeric authority for how long WINDUP/RECOVERY last), not a
 * separately-calibrated WINDUP/ACTIVE/RECOVERY numeric profile. See
 * `docs/gameplay/weapon-primary-basic-attack-contract.md` §16.
 */

import type { BalanceWeaponId } from '../reference/weapon-progression-v0.js';

export interface WeaponBasicAttackTimingProfile {
  readonly id: BalanceWeaponId;
  /** Pre-hit share of the source-owned ordinary cycle. */
  readonly castShareOfCycle: number;
  /** Baseline retreat speed used by the Balance Lab combat kernel. */
  readonly baseRetreatSpeedMps: number;
}

/**
 * The sword's original 0.22s reference pre-hit is shortened by 1.5x.
 * Assassin's reference pre-hit is 0.18s of its 2.60s cycle. The visible slash
 * crosses the 240-degree hit range centred on the strike, so this windup also
 * sets the slash speed; the cycle itself is unchanged.
 * Original reference timings are retained except SWORD and GREATSWORD.
 * Sword's 0.22s reference pre-hit is shortened by 1.5x. Greatsword's pre-hit
 * reference is shortened from 0.45s to 0.30s at the 2.50s reference cycle;
 * its actual basic cycle is still resolved by source + basic-only rate tuning.
 */
export const WEAPON_BASIC_ATTACK_TIMING_V0: Readonly<Record<BalanceWeaponId, WeaponBasicAttackTimingProfile>> = Object.freeze({
  DAGGER_PAIR: Object.freeze({ id: 'DAGGER_PAIR', castShareOfCycle: 0.10 / (1 / 1.85), baseRetreatSpeedMps: 1.05 }),
  SWORD: Object.freeze({ id: 'SWORD', castShareOfCycle: (0.22 / 1.5) / (1 / 0.72), baseRetreatSpeedMps: 0.80 }),
  HEAVY_AXE: Object.freeze({ id: 'HEAVY_AXE', castShareOfCycle: 0.35 / (1 / 0.56), baseRetreatSpeedMps: 0.55 }),
  SPEAR: Object.freeze({ id: 'SPEAR', castShareOfCycle: 0.18 / (1 / 0.90), baseRetreatSpeedMps: 0.75 }),
  BOW_REFERENCE: Object.freeze({ id: 'BOW_REFERENCE', castShareOfCycle: 0.18 / (1 / 1.70), baseRetreatSpeedMps: 0.95 }),
  ASSASSIN_BLADE: Object.freeze({ id: 'ASSASSIN_BLADE', castShareOfCycle: 0.18 / 2.60, baseRetreatSpeedMps: 1.00 }),
  GREATSWORD: Object.freeze({ id: 'GREATSWORD', castShareOfCycle: 0.30 / 2.50, baseRetreatSpeedMps: 0.50 }),
});

export interface BasicAttackPhaseTiming {
  /** Duration of the WINDUP phase, ending at the authoritative strike (the
   * single-tick ACTIVE phase, carved out of `postHitS` below — see
   * `BasicAttackRuntimeState` in `basic-attack.ts`). */
  readonly preHitS: number;
  /** Duration covering ACTIVE + the following RECOVERY phase
   * (Skill-cancelable once RECOVERY begins). */
  readonly postHitS: number;
  /** Source-owned full ordinary cycle. `preHitS + postHitS === cycleS`. */
  readonly cycleS: number;
}

/**
 * Split the source-owned ordinary cycle into the WINDUP/RECOVERY timing that
 * drives the WINDUP/ACTIVE/RECOVERY runtime phase machine, using the weapon
 * `castShareOfCycle` compatibility-seed bridge. This never changes cadence.
 */
export function resolveBasicAttackPhaseTiming(weaponId: BalanceWeaponId, cycleS: number): BasicAttackPhaseTiming {
  if (!(Number.isFinite(cycleS) && cycleS > 0)) throw new Error('resolveBasicAttackPhaseTiming: cycleS must be finite > 0');
  const share = WEAPON_BASIC_ATTACK_TIMING_V0[weaponId].castShareOfCycle;
  const preHitS = cycleS * share;
  return { preHitS, postHitS: cycleS - preHitS, cycleS };
}
